// Copyright (c) StrangeBee 2023

import { classList } from '@Helpers/className';
import { Menu } from 'antd';
import React, { Dispatch, ReactElement, ReactNode, SetStateAction } from 'react';

import './THVisualizer.scss';

interface RenderItemProps<T> {
    itemClassName?: string;
    itemKey: string;
    item: T;
    children: (item: T) => ReactElement;
    setSelected: Dispatch<SetStateAction<T | undefined>>;
}
const renderItem = <T extends { [key: string]: any }>({
    itemClassName,
    itemKey,
    item,
    children,
    setSelected,
}: RenderItemProps<T>): ReactElement => (
    <Menu.Item
        key={item[itemKey]}
        className={classList('menu__item', itemClassName)}
        onClick={() => {
            setSelected(item);
        }}
    >
        {children(item)}
    </Menu.Item>
);

interface THVisualizerProps<T extends { [key: string]: any }> {
    dataSource: T[] | Record<string, T[]>;
    children: (item: T) => ReactElement;
    title: ReactNode;
    filter: ReactNode;
    content: ReactNode;
    menuAction?: ReactNode;
    itemKey: string;
    itemClassName?: string;
    selectedState: {
        selected: T | undefined;
        setSelected: React.Dispatch<React.SetStateAction<T | undefined>>;
    };
    openKeysState: {
        openKeys: string[] | undefined;
        setOpenKeys: React.Dispatch<React.SetStateAction<string[] | undefined>>;
    };
    className?: string;
    hideMenu?: boolean;
}

export const THVisualizer = <T extends { [key: string]: any }>({
    dataSource,
    children,
    selectedState,
    openKeysState,
    title,
    filter,
    content,
    menuAction,
    className,
    itemKey,
    itemClassName,
    hideMenu,
}: THVisualizerProps<T>): ReactElement => {
    const { openKeys, setOpenKeys } = openKeysState;
    const { selected, setSelected } = selectedState;

    return (
        <div className={classList('th-visualizer', className)}>
            <div className="th-visualizer__viewer">
                <div className="th-visualizer__viewer__header">{title}</div>
                <div className="th-visualizer__viewer__content">{content}</div>
            </div>
            {!hideMenu && (
                <div className="th-visualizer__explorer">
                    <div className="th-visualizer__explorer__header">{filter}</div>
                    <Menu
                        className="th-visualizer__explorer__menu"
                        mode="inline"
                        selectedKeys={[selected?.[itemKey]]}
                        openKeys={openKeys}
                    >
                        {Array.isArray(dataSource)
                            ? dataSource.map((item) =>
                                  renderItem<T>({ item, children, setSelected, itemKey, itemClassName }),
                              )
                            : Object.entries(dataSource).map(([category, items]) => {
                                  return (
                                      <Menu.SubMenu
                                          className="menu__item-group"
                                          key={category}
                                          title={category}
                                          onTitleClick={(): void => {
                                              if (openKeys?.includes(category)) {
                                                  setOpenKeys(openKeys?.filter((value) => value !== category));
                                              } else {
                                                  setOpenKeys([...(openKeys || []), category]);
                                              }
                                          }}
                                      >
                                          {items.map((item) =>
                                              renderItem<T>({
                                                  item,
                                                  children,
                                                  setSelected,
                                                  itemKey,
                                                  itemClassName,
                                              }),
                                          )}
                                      </Menu.SubMenu>
                                  );
                              })}
                    </Menu>
                    <div className="th-visualizer__explorer__menu-actions">{menuAction}</div>
                </div>
            )}
        </div>
    );
};
